import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import adminPricingService, {
  PricingPlan,
} from "../../services/adminPricingService";
import landingService, {
  LandingContent,
  SectionConfig,
} from "../../services/landingService";
import "./LandingPage.css";
import {
  ArrowUp,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Play,
  Sun,
  Moon,
  Check,
  Info,
  X,
} from "lucide-react";
import TermsModal from "../Common/TermsModal";
import PlanCheckoutModal from "./PlanCheckoutModal";
import { useTheme } from "../../contexts/ThemeContext";
import { renderLandingIcon } from "./landingIcons";
import {
  DEFAULT_LANDING_CONTENT,
  cssUrl,
  isSafeLink,
  mergeLandingContent,
  resolveImage,
  sectionAnchor,
  toEmbedUrl,
} from "./landingDefaults";

import { LANDING_CACHE_KEY as CACHE_KEY } from "./landingDefaults";

const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

// One place that decides what a configured link does: "#id" scrolls to a
// section, "/path" is an in-app route, http(s)/mailto/tel open normally.
// Anything else (including javascript: URLs) is ignored.
const useGoTo = () => {
  const navigate = useNavigate();
  return useCallback(
    (link: string) => {
      const target = (link || "").trim();
      if (!target || !isSafeLink(target)) return;
      if (target.startsWith("#")) scrollToSection(target.slice(1));
      else if (target.startsWith("/")) navigate(target);
      else if (/^https?:\/\//i.test(target))
        window.open(target, "_blank", "noopener,noreferrer");
      else window.location.href = target;
    },
    [navigate]
  );
};

const SimpleLink: React.FC<{
  to: string;
  className?: string;
  children: React.ReactNode;
}> = ({ to, className, children }) => {
  const goTo = useGoTo();
  return (
    <button type="button" className={className} onClick={() => goTo(to)}>
      {children}
    </button>
  );
};

const readCachedContent = (): LandingContent | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? mergeLandingContent(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

// Shown immediately while the live plans load, and kept as a fallback
// if the pricing API is unreachable -- the section should never render empty.
const fallbackPricingPlans: PricingPlan[] = [
  {
    id: -1,
    name: "Single Auction",
    tagline: "One-off sale, no subscription",
    price: "₹999",
    period: "/ auction",
    badge: null,
    cta_text: "Get Started",
    features: [
      "1 live auction listing",
      "Unlimited participants",
      "Real-time bidding engine",
      "Downloadable auction report",
    ],
  },
  {
    id: -2,
    name: "Monthly",
    tagline: "For regular sellers",
    price: "₹2,499",
    period: "/ month",
    badge: "Most Popular",
    cta_text: "Start Free Trial",
    features: [
      "Unlimited auctions",
      "All auction formats",
      "Advanced analytics",
      "Priority support",
    ],
  },
  {
    id: -3,
    name: "Yearly",
    tagline: "Best value — 2 months free",
    price: "₹24,999",
    period: "/ year",
    badge: "Best Value",
    cta_text: "Start Yearly Plan",
    features: [
      "Everything in Monthly",
      "Dedicated account manager",
      "Bulk tools & early access",
      "2 months free savings",
    ],
  },
];

// The visitor's dismissal lasts for the browser session and is tied to the
// message text, so an edited announcement shows up again.
const ANNOUNCE_DISMISS_KEY = "lp-announcement-dismissed";

const LandingPageView: React.FC<{ content: LandingContent }> = ({
  content,
}) => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<PricingPlan | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const { announcement } = content;
  const [dismissedAnnouncement, setDismissedAnnouncement] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(ANNOUNCE_DISMISS_KEY);
    } catch {
      return null;
    }
  });
  const showAnnouncement =
    announcement.enabled &&
    announcement.text.trim() !== "" &&
    dismissedAnnouncement !== announcement.text;
  const announceRef = useRef<HTMLDivElement>(null);
  const [announceHeight, setAnnounceHeight] = useState(0);

  // The bar is fixed, so the nav and hero are pushed down by its measured
  // height (it wraps onto more lines on phones).
  useEffect(() => {
    const el = announceRef.current;
    if (!showAnnouncement || !el) {
      setAnnounceHeight(0);
      return;
    }
    const update = () => setAnnounceHeight(el.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showAnnouncement, announcement.text]);

  const dismissAnnouncement = () => {
    setDismissedAnnouncement(announcement.text);
    try {
      sessionStorage.setItem(ANNOUNCE_DISMISS_KEY, announcement.text);
    } catch {
      // storage unavailable -- it just comes back on the next visit
    }
  };
  const { theme, toggleTheme } = useTheme();
  const goTo = useGoTo();
  const navigate = useNavigate();
  const themeToggleLabel =
    theme === "light" ? "Switch to dark theme" : "Switch to light theme";

  const { nav, hero, about, features, videos, howItWorks, pricing, footer } =
    content;
  const enabledSections = content.sections.filter((s) => s.enabled);
  const slides = hero.slides;
  const aboutEnabled = enabledSections.some((s) => s.type === "about");
  const renderedAnchors = new Set(enabledSections.map(sectionAnchor));

  // "Who We Are" section plays its reveal animation once, the first time it
  // scrolls into view, instead of on page load (it usually sits below the
  // fold, so a load-time animation would already be finished by the time
  // anyone sees it).
  const aboutSectionRef = useRef<HTMLDivElement>(null);
  const [isAboutVisible, setIsAboutVisible] = useState(false);

  useEffect(() => {
    const node = aboutSectionRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsAboutVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAboutVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [aboutEnabled]);

  const [pricingPlans, setPricingPlans] =
    useState<PricingPlan[]>(fallbackPricingPlans);

  useEffect(() => {
    let cancelled = false;
    adminPricingService
      .getPublicPlans()
      .then((plans) => {
        if (!cancelled && plans.length > 0) setPricingPlans(plans);
      })
      .catch(() => {
        // keep the fallback plans already shown
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The extra card (Enterprise by default) is always appended to the live
  // plans, even when the backend only has the regular tiers.
  const extra = pricing.extraPlan;
  const visiblePlans: PricingPlan[] = extra.enabled
    ? [
        ...pricingPlans,
        {
          id: -4,
          name: extra.name,
          tagline: extra.tagline,
          price: extra.price,
          period: extra.period,
          badge: extra.badge || null,
          cta_text: extra.cta_text,
          cta_link: extra.cta_link,
          features: extra.features,
        },
      ]
    : pricingPlans;

  // Real (DB-backed) plans have positive ids and can be bought right here,
  // without logging in: the visitor gives the phone number and email the plan
  // belongs to. The fallback and Enterprise cards use negative ids and keep
  // their configured link.
  const handlePlanCta = (plan: PricingPlan) => {
    if (plan.id <= 0) {
      goTo(plan.cta_link || pricing.ctaLink || "/register");
      return;
    }
    setCheckoutPlan(plan);
  };

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      setIsNavScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, Math.max(2, hero.slideIntervalSec || 6) * 1000);
    return () => clearInterval(timer);
  }, [slides.length, hero.slideIntervalSec]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderHero = (section: SectionConfig) => {
    const slide = slides[activeSlide] || slides[0];
    return (
      <section key={section.id} id={sectionAnchor(section)} className="hero-section">
        {slides.map((s, index) => (
          <div
            key={index}
            className={`hero-bg-image ${
              index === activeSlide ? "is-active" : ""
            }`}
            style={{ backgroundImage: cssUrl(resolveImage(s.image)) }}
            role="img"
            aria-label={s.alt}
            aria-hidden={index !== activeSlide}
          />
        ))}
        <div className="hero-bg-overlay" />

        <div className="hero-container">
          <div className="hero-grid">
            <div className="hero-text-col">
              {slide && (
                /* keyed on the slide so the copy re-runs its fade-in on change */
                <div key={activeSlide} className="hero-copy">
                  <h1 className="hero-title">
                    {slide.titleLead.map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                    <span className="hero-highlight">
                      {slide.titleHighlight}
                    </span>
                  </h1>

                  <p className="hero-subtitle">{slide.subtitle}</p>
                </div>
              )}

              <div className="hero-buttons">
                {hero.primaryButton.text && (
                  <SimpleLink
                    to={hero.primaryButton.link}
                    className="btn-hero-primary"
                  >
                    {hero.primaryButton.text} <ArrowRight className="w-4 h-4" />
                  </SimpleLink>
                )}
                {hero.secondaryButton.enabled && hero.secondaryButton.text && (
                  <button
                    type="button"
                    className="btn-hero-secondary"
                    onClick={() => goTo(hero.secondaryButton.link)}
                  >
                    <Play className="w-4 h-4" /> {hero.secondaryButton.text}
                  </button>
                )}
              </div>

              <div className="hero-stats-row">
                {hero.stats.map((stat, index) => (
                  <div key={index} className="hero-stat">
                    <div className="hero-stat-icon">
                      {renderLandingIcon(stat.icon, "w-5 h-5", 1.75)}
                    </div>
                    <div className="hero-stat-copy">
                      <div className="hero-stat-number">{stat.number}</div>
                      <div className="hero-stat-label">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="hero-bottom-strip">
            {hero.categories.map((category, index) => (
              <div
                key={index}
                className="hero-strip-card"
                style={{ backgroundImage: cssUrl(resolveImage(category.image)) }}
              >
                <div className="hero-strip-overlay" />
                <div className="hero-strip-icon">
                  {renderLandingIcon("Gavel", "w-4 h-4")}
                </div>
                <div className="hero-strip-title">{category.title}</div>
                <button
                  type="button"
                  className="hero-strip-arrow"
                  aria-label={`Browse ${category.title}`}
                  onClick={() => goTo(category.link)}
                >
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderAbout = (section: SectionConfig) => (
    <section key={section.id} id={sectionAnchor(section)} className="about-section">
      {/* Same treatment as the hero above: a full-bleed background photo
          with a gradient overlay the copy sits on top of, instead of a
          separate boxed image beside the text. */}
      <div
        className="about-bg-image"
        style={{ backgroundImage: cssUrl(resolveImage(about.image)) }}
        role="img"
        aria-label={about.imageAlt}
      />
      <div className="about-bg-overlay" />

      <div className="container">
        <div
          ref={aboutSectionRef}
          className={`about-content ${isAboutVisible ? "is-visible" : ""}`}
        >
          <h2 className="about-title about-anim">{about.title}</h2>
          {about.paragraphs.map((text, i) => (
            <p key={i} className="about-text about-anim">
              {text}
            </p>
          ))}
          {about.chips.length > 0 && (
            <div className="about-highlights about-anim">
              {about.chips.map((chip, i) => (
                <span key={i} className="about-highlight-chip">
                  {renderLandingIcon(chip.icon, "w-3 h-3")}
                  {chip.text}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );

  const renderSectionHeading = (title: string, subtitle: string) => (
    <div className="text-center loading-fade-in check">
      <h2 className="text-4xl font-bold text-gray-900 mb-4">{title}</h2>
      {subtitle && (
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">{subtitle}</p>
      )}
    </div>
  );

  const renderFeatures = (section: SectionConfig) => (
    <section key={section.id} id={sectionAnchor(section)} className="features-section">
      <div className="container">
        {renderSectionHeading(features.title, features.subtitle)}
        <div className="features-grid">
          {features.items.map((feature, index) => (
            <div
              key={index}
              className="feature-card loading-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="feature-icon">
                {renderLandingIcon(feature.icon, "w-8 h-8")}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderVideos = (section: SectionConfig) => (
    <section key={section.id} id={sectionAnchor(section)} className="how-to-register-section">
      <div className="container">
        {renderSectionHeading(videos.title, videos.subtitle)}
        <div className="videos-grid loading-fade-in" style={{ animationDelay: "0.2s" }}>
          {videos.items.map((video, index) => {
            const src = toEmbedUrl(video.url);
            if (!src) return null;
            return (
              <div key={index} className="video-item">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                  {video.title}
                </h3>
                <div className="video-wrapper">
                  <iframe
                    width="100%"
                    height="250"
                    src={src}
                    title={`${video.title} - video player`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="registration-video"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );

  const renderHowItWorks = (section: SectionConfig) => (
    <section key={section.id} id={sectionAnchor(section)} className="how-it-works">
      <div className="container">
        {renderSectionHeading(howItWorks.title, howItWorks.subtitle)}
        <div className="steps-container">
          {howItWorks.steps.map((step, index) => (
            <div
              key={index}
              className="step-card loading-fade-in"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="step-number">{index + 1}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderPricing = (section: SectionConfig) => (
    <section key={section.id} id={sectionAnchor(section)} className="pricing-section">
      <div className="container">
        {renderSectionHeading(pricing.title, pricing.subtitle)}

        <div className="pricing-grid">
          {visiblePlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`pricing-card loading-fade-in ${
                plan.badge === "Most Popular" ? "pricing-card-highlighted" : ""
              } ${plan.badge === "Channel Partner" ? "pricing-card-enterprise" : ""}`}
              style={{ animationDelay: `${index * 0.12}s` }}
            >
              {plan.badge && <span className="pricing-badge">{plan.badge}</span>}
              <h3 className="pricing-plan-name">{plan.name}</h3>
              <p className="pricing-plan-tagline">{plan.tagline}</p>
              <div className="pricing-price-row">
                <span className="pricing-price">{plan.price}</span>
                <span className="pricing-period">{plan.period}</span>
              </div>
              <ul className="pricing-feature-list">
                {plan.features.map((feature) => (
                  <li key={feature}>
                    <Check className="w-4 h-4" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="pricing-cta-btn"
                onClick={() => handlePlanCta(plan)}
              >
                {plan.cta_text}
              </button>
            </div>
          ))}
        </div>

        {pricing.footnote && <p className="pricing-footnote">{pricing.footnote}</p>}
      </div>
    </section>
  );

  const renderCustom = (section: SectionConfig) => {
    const custom = content.customSections[section.id];
    if (!custom) return null;
    const image = resolveImage(custom.image);
    return (
      <section key={section.id} id={sectionAnchor(section)} className="custom-section">
        <div className="container">
          <div
            className={`custom-section-inner ${
              image ? `has-image image-${custom.imagePosition}` : ""
            }`}
          >
            <div className="custom-section-copy">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {custom.title}
              </h2>
              {custom.body
                .split(/\n{2,}/)
                .filter((p) => p.trim())
                .map((p, i) => (
                  <p key={i} className="custom-section-text">
                    {p}
                  </p>
                ))}
              {custom.buttonText && (
                <SimpleLink to={custom.buttonLink} className="btn-hero-primary">
                  {custom.buttonText} <ArrowRight className="w-4 h-4" />
                </SimpleLink>
              )}
            </div>
            {image && (
              <div className="custom-section-media">
                <img src={image} alt={custom.title} />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  const renderSection = (section: SectionConfig) => {
    switch (section.type) {
      case "hero":
        return renderHero(section);
      case "about":
        return renderAbout(section);
      case "features":
        return renderFeatures(section);
      case "videos":
        return renderVideos(section);
      case "how_it_works":
        return renderHowItWorks(section);
      case "pricing":
        return renderPricing(section);
      case "custom":
        return renderCustom(section);
      default:
        return null;
    }
  };

  const renderFooterLink = (link: {
    label: string;
    kind: string;
    target: string;
  }) => {
    switch (link.kind) {
      case "section":
        return renderedAnchors.has(link.target) ? (
          <button type="button" onClick={() => scrollToSection(link.target)}>
            {link.label}
          </button>
        ) : null;
      case "route":
        return <SimpleLink to={link.target}>{link.label}</SimpleLink>;
      case "url":
        return isSafeLink(link.target) ? (
          <a href={link.target} target="_blank" rel="noopener noreferrer">
            {link.label}
          </a>
        ) : null;
      case "terms":
        return (
          <button type="button" onClick={() => setIsTermsModalOpen(true)}>
            {link.label}
          </button>
        );
      default:
        return <span>{link.label}</span>;
    }
  };

  return (
    <div
      className="landing-page min-h-screen"
      style={
        showAnnouncement
          ? ({ "--lp-announce-h": `${announceHeight}px` } as React.CSSProperties)
          : undefined
      }
    >
      {showAnnouncement && (
        <div className="lp-announcement" role="status" ref={announceRef}>
          <Info className="lp-announcement-icon" aria-hidden="true" />
          {/* Running label: the message is repeated so the loop has no gap.
              The second copy is hidden from screen readers. */}
          <div
            className="lp-marquee"
            style={
              {
                "--lp-marquee-duration": `${Math.max(18, Math.round(announcement.text.length * 0.16))}s`,
              } as React.CSSProperties
            }
          >
            <div className="lp-marquee-track">
              <p className="lp-marquee-group">{announcement.text}</p>
              <p className="lp-marquee-group" aria-hidden="true">
                {announcement.text}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="lp-announcement-close"
            onClick={dismissAnnouncement}
            aria-label="Dismiss announcement"
          >
            <X aria-hidden="true" />
          </button>
        </div>
      )}
      <header className={`site-nav ${isNavScrolled ? "is-scrolled" : ""}`}>
        <div className="site-nav-inner">
          <button
            type="button"
            className="hero-brand"
            onClick={() => navigate("/")}
            aria-label={nav.logoAlt}
          >
            <img
              src={resolveImage(nav.logo)}
              alt={nav.logoAlt}
              className="hero-brand-image"
            />
          </button>

          <div className="site-nav-actions">
            <button
              type="button"
              className="lp-theme-toggle"
              onClick={toggleTheme}
              aria-label={themeToggleLabel}
              title={themeToggleLabel}
            >
              {theme === "light" ? <Moon /> : <Sun />}
            </button>
            {nav.showTrialButton && nav.trialText && (
              <SimpleLink to={nav.trialLink} className="nav-trial-btn">
                {nav.trialText}
              </SimpleLink>
            )}
            {nav.loginText && (
              <SimpleLink to={nav.loginLink} className="hero-login-link">
                {nav.loginText}
              </SimpleLink>
            )}
          </div>
        </div>
      </header>

      {enabledSections.map(renderSection)}

      {/* Terms Modal */}
      <PlanCheckoutModal plan={checkoutPlan} onClose={() => setCheckoutPlan(null)} />
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />

      {/* Footer */}
      <footer id="contact" className="footer">
        {/* CTA band sits above the fold line, not inside it -- the footer's
            one job most visitors will actually use */}
        {footer.cta.enabled && (
          <div className="footer-cta">
            <div className="container footer-cta-inner">
              <div>
                <h2>{footer.cta.title}</h2>
                <p>{footer.cta.text}</p>
              </div>
              {footer.cta.buttonText && (
                <SimpleLink to={footer.cta.buttonLink} className="footer-cta-button">
                  {footer.cta.buttonText} <ArrowRight className="w-4 h-4" />
                </SimpleLink>
              )}
            </div>
          </div>
        )}

        <div className="container">
          <div className="footer-top">
            <div className="footer-brand-col">
              <div className="footer-logo">
                <img
                  src={resolveImage(footer.logo)}
                  alt={nav.logoAlt}
                  className="footer-logo-image"
                />
              </div>
              <p className="footer-tagline">{footer.tagline}</p>
              <ul className="footer-contact-list">
                {footer.email && (
                  <li>
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <a href={`mailto:${footer.email}`}>{footer.email}</a>
                  </li>
                )}
                {footer.phone && (
                  <li>
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <a href={`tel:${footer.phone.replace(/[^\d+]/g, "")}`}>
                      {footer.phone}
                    </a>
                  </li>
                )}
                {footer.address && (
                  <li>
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{footer.address}</span>
                  </li>
                )}
              </ul>
            </div>

            <div className="footer-links-grid">
              {footer.columns.map((column, ci) => {
                const items = column.links
                  .map((link, li) => ({ li, node: renderFooterLink(link) }))
                  .filter((item) => item.node);
                if (items.length === 0) return null;
                return (
                  <div key={ci} className="footer-section">
                    <h3>{column.title}</h3>
                    <ul>
                      {items.map((item) => (
                        <li key={item.li}>{item.node}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="footer-bottom">
            <p>{footer.copyright}</p>
            {footer.showStats && (
              <ul className="footer-bottom-stats">
                {hero.stats.map((stat, index) => (
                  <li key={index}>
                    <strong>{stat.number}</strong> {stat.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button
        className={`scroll-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </div>
  );
};

// Loads the admin-edited content. The last good copy is cached in
// localStorage so a returning visitor sees the real page immediately instead
// of a flash of the built-in defaults; a first-time visitor sees a blank page
// for the length of one request.
const LandingPage: React.FC = () => {
  const [content, setContent] = useState<LandingContent | null>(
    readCachedContent
  );

  useEffect(() => {
    let cancelled = false;
    landingService
      .getPublicContent()
      .then((stored) => {
        if (cancelled) return;
        try {
          if (stored) localStorage.setItem(CACHE_KEY, JSON.stringify(stored));
          else localStorage.removeItem(CACHE_KEY);
        } catch {
          // storage unavailable -- cache is only a nicety
        }
        setContent(mergeLandingContent(stored));
      })
      .catch(() => {
        if (!cancelled) setContent((prev) => prev ?? DEFAULT_LANDING_CONTENT);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!content) return <div className="landing-page min-h-screen" />;
  return <LandingPageView content={content} />;
};

export default LandingPage;
